/**
 * Linda Lookup Tool Handlers — Client-Side Execution
 *
 * Executes lookup tools from the browser. Returns dual result:
 * toolResultJson (JSON for Claude) + searchResults/notification (for UI).
 * Adapted from deleted Next.js tool-handlers/ (git e2751bd6).
 */

import {logger} from '@/lib/logger';
import {mbApiJson, fetchWithTimeout} from '@/util/request-utils';
import type {
    SearchResults,
    SearchResult,
} from '@/components/linda-chat-panel/types';

const log = logger.tag('LindaTools');

export interface LookupToolResult {
    toolResultJson: string;
    searchResults?: SearchResults;
    notification?: {
        type: 'video' | 'silo' | '360-spin';
        status: 'processing' | 'completed' | 'failed' | 'cancelled';
        title: string;
        message: string;
        productId?: string;
        productName?: string;
        requestId?: string;
        assetType?: 'product' | 'group';
    };
}

interface LookupResultBase {
    results: Array<{
        id: string;
        name: string;
        sku?: string;
        thumbnail?: string;
    }>;
    count: number;
    totalFound: number;
    hasMore: boolean;
    message: string;
}

type LookupResult = SearchResult;

interface Tokens {
    modelbank_token: string;
    auth_token: string;
}

export async function executeLookupTool(
    toolName: string,
    args: Record<string, unknown>,
    tokens: Tokens,
    selectedSupplierId?: string
): Promise<LookupToolResult> {
    try {
        switch (toolName) {
            case 'lookup_product':
                return await handleLookupProduct(
                    args as {query: string; limit?: number},
                    tokens
                );
            case 'lookup_group':
                return await handleLookupGroup(
                    args as {query: string; limit?: number},
                    tokens,
                    selectedSupplierId
                );
            case 'lookup_material':
                return handleLookupMaterial(args as {query: string});
            case 'ai_search_products':
                return await handleAiSearchProducts(
                    args as {query: string; limit?: number},
                    tokens,
                    selectedSupplierId
                );
            case 'generate_renders':
                return await handleGenerateRenders(
                    args as {
                        productId: string;
                        renderType: 'silo' | '360-spin';
                        allVariants: boolean;
                        message: string;
                    },
                    tokens
                );
            default:
                return {
                    toolResultJson: JSON.stringify({
                        error: `Unknown lookup tool: ${toolName}`,
                        results: [],
                        count: 0,
                    }),
                };
        }
    } catch (error) {
        log.error(`Tool ${toolName} failed`, error);
        return {
            toolResultJson: JSON.stringify({
                error: `Failed to search database`,
                results: [],
                count: 0,
            }),
        };
    }
}

interface ProductHit {
    _id: string;
    uid?: string;
    _source: {
        name: string;
        sku: string;
        model: string;
        level?: number;
        [key: string]: unknown;
    };
    [key: string]: unknown;
}

interface ProductSearchResponse {
    hits: {
        hits: ProductHit[];
        total: number;
    };
}

async function handleLookupProduct(
    input: {query: string; limit?: number},
    tokens: Tokens
): Promise<LookupToolResult> {
    const displayLimit = 6;

    const data = await mbApiJson<ProductSearchResponse>(`/manage/products`, {
        token: tokens.modelbank_token,
        auth_token: tokens.auth_token,
        params: {
            per_page: 50,
            query: input.query,
        },
    });

    const items = data.hits.hits;

    // Deduplicate by base product ID (strip variant suffix)
    const seenIds = new Set<string>();
    const unique = items.filter((p) => {
        const baseId = p._id.split('_')[0];
        if (seenIds.has(baseId)) return false;
        seenIds.add(baseId);
        return true;
    });

    const results: LookupResult[] = unique.slice(0, displayLimit).map((p) => ({
        id: (p.uid ?? p._id).split('_')[0],
        name: p._source.name,
        sku: p._source.sku,
        thumbnail: getProductThumbnail(p._source.model ?? p._id, undefined),
    }));

    const totalFound = unique.length;
    const hasMore = totalFound > displayLimit;

    const jsonResult: LookupResultBase = {
        results,
        count: Math.min(displayLimit, totalFound),
        totalFound,
        hasMore,
        message: hasMore
            ? `Showing ${results.length} of ${totalFound}+ results.`
            : `Found ${totalFound} results.`,
    };

    return {
        toolResultJson: JSON.stringify(jsonResult),
        searchResults:
            results.length > 0 ? {type: 'product', results} : undefined,
    };
}

interface GroupResult {
    id: number;
    name: string;
    thumb?: {thumb3D?: string; thumb2D?: string};
}

interface GroupListResponse {
    results?: GroupResult[];
}

async function handleLookupGroup(
    input: {query: string; limit?: number},
    tokens: Tokens,
    selectedSupplierId?: string
): Promise<LookupToolResult> {
    const displayLimit = 6;

    const params: Record<string, string | number> = {
        per_page: 100,
        sort_order: 'desc',
    };
    if (selectedSupplierId) params.supplier_id = selectedSupplierId;
    if (input.query) params.name = input.query;

    const data = await mbApiJson<GroupListResponse>(
        `/manage/product_groups/list_admin`,
        {
            token: tokens.modelbank_token,
            auth_token: tokens.auth_token,
            params,
        }
    );

    const groups = data.results ?? [];
    const totalFound = groups.length;
    const hasMore = totalFound > displayLimit;

    const results: LookupResult[] = groups.slice(0, displayLimit).map((g) => ({
        id: String(g.id),
        name: g.name,
        thumbnail: g.thumb?.thumb3D ?? g.thumb?.thumb2D,
    }));

    const jsonResult: LookupResultBase = {
        results,
        count: Math.min(displayLimit, totalFound),
        totalFound,
        hasMore,
        message: hasMore
            ? `Showing ${results.length} of ${totalFound}+ results.`
            : `Found ${totalFound} results.`,
    };

    return {
        toolResultJson: JSON.stringify(jsonResult),
        searchResults:
            results.length > 0 ? {type: 'group', results} : undefined,
    };
}

function handleLookupMaterial(input: {query: string}): LookupToolResult {
    return {
        toolResultJson: JSON.stringify({
            results: [],
            count: 0,
            totalFound: 0,
            hasMore: false,
            message: `Material search "${input.query}" - please use the Materials page for detailed search.`,
        }),
    };
}

interface Gpu1SearchResult {
    id: string;
    name?: string;
    sku?: string;
    thumbnail?: string;
    [key: string]: unknown;
}

interface Gpu1SearchResponse {
    results?: Gpu1SearchResult[];
}

async function handleAiSearchProducts(
    input: {query: string; limit?: number},
    tokens: Tokens,
    selectedSupplierId?: string
): Promise<LookupToolResult> {
    const displayLimit = 6;

    // gpu1.floorplanner.com/search — semantic search endpoint
    const gpu1Base = import.meta.env.DEV
        ? '/gpu1'
        : 'https://gpu1.floorplanner.com';

    const response = await fetchWithTimeout(
        `${gpu1Base}/search`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${tokens.modelbank_token}`,
            },
            body: JSON.stringify({
                query: input.query,
                supplier_id: selectedSupplierId
                    ? Number(selectedSupplierId)
                    : undefined,
            }),
        },
        30000
    );

    if (!response.ok) {
        throw new Error(`AI search failed: ${response.status}`);
    }

    const data = (await response.json()) as Gpu1SearchResponse;
    const items = data.results ?? [];

    // Deduplicate by base product ID
    const seenIds = new Set<string>();
    const unique = items.filter((p) => {
        const baseId = String(p.id).split('_')[0];
        if (seenIds.has(baseId)) return false;
        seenIds.add(baseId);
        return true;
    });

    const results: LookupResult[] = unique.slice(0, displayLimit).map((p) => ({
        id: String(p.id).split('_')[0],
        name: p.name ?? 'Unknown product',
        sku: p.sku,
        thumbnail: p.thumbnail ?? getProductThumbnail(p.id, undefined),
    }));

    const totalFound = unique.length;
    const hasMore = totalFound > displayLimit;

    const jsonResult: LookupResultBase = {
        results,
        count: Math.min(displayLimit, totalFound),
        totalFound,
        hasMore,
        message: hasMore
            ? `Showing ${results.length} of ${totalFound}+ results using AI semantic search.`
            : `Found ${totalFound} results using AI semantic search.`,
    };

    return {
        toolResultJson: JSON.stringify(jsonResult),
        searchResults:
            results.length > 0 ? {type: 'product', results} : undefined,
    };
}

interface JobResponse {
    id?: string;
    [key: string]: unknown;
}

async function handleGenerateRenders(
    input: {
        productId: string;
        renderType: 'silo' | '360-spin';
        allVariants: boolean;
        message: string;
    },
    tokens: Tokens
): Promise<LookupToolResult> {
    const {productId, renderType, allVariants} = input;

    // Fetch the product to verify it exists and get its name
    const productData = await mbApiJson<ProductSearchResponse>(
        `/manage/products`,
        {
            token: tokens.modelbank_token,
            auth_token: tokens.auth_token,
            params: {per_page: 1, query: productId},
        }
    );

    const product = productData.hits.hits[0];

    // Abort if product not found (matches original behavior)
    if (!product) {
        return {
            toolResultJson: JSON.stringify({
                success: false,
                error: `Product not found with ID: ${productId}`,
            }),
        };
    }

    const productName = product._source?.name ?? `Product ${productId}`;

    const mediaKind =
        renderType === 'silo' ? 'Products::Silo' : 'Products::Spin360';

    const settings = {
        all_variants: allVariants,
        width: 2000,
        height: 2000,
        views: renderType === 'silo' ? ['iso', 'front', 'left', 'top'] : [],
        settings: {
            fmt: 'png',
            shadows: true,
            background: null,
            margins: null,
        },
    };

    const exportResponse = await mbApiJson<JobResponse>('/jobs.json', {
        method: 'POST',
        token: tokens.modelbank_token,
        auth_token: tokens.auth_token,
        body: {
            job: {
                media_kind: mediaKind,
                asset_type: 'Product',
                asset_id: productId,
                settings,
            },
        },
    });

    if (!exportResponse?.id) {
        return {
            toolResultJson: JSON.stringify({
                success: false,
                error: 'Render generation API returned no job ID',
            }),
        };
    }

    const isSilo = renderType === 'silo';
    const renderLabel = isSilo ? 'silo renders' : '360 spin';
    const notification = {
        type: isSilo ? ('silo' as const) : ('360-spin' as const),
        status: 'processing' as const,
        title: isSilo ? 'Generating Silo Renders' : 'Generating 360 Spin',
        message: `Creating ${renderLabel} for ${productName}...`,
        productId,
        productName,
        requestId: exportResponse.id,
        assetType: 'product' as const,
    };

    return {
        toolResultJson: JSON.stringify({
            success: true,
            jobId: exportResponse.id,
            productName,
            productId,
            renderType,
            notification,
        }),
        notification,
    };
}

function getProductThumbnail(
    productId: string,
    thumb?: {thumb3D?: string; thumb2D?: string}
): string {
    if (thumb?.thumb3D) return thumb.thumb3D;
    if (thumb?.thumb2D) return thumb.thumb2D;

    const id = String(productId).split('_')[0];
    if (id) {
        const hash = id.slice(0, 2);
        return `https://d2bi8gvwsa8xa3.cloudfront.net/cdb/renders/${hash}/${id}.perspective.540.png`;
    }

    return '';
}
